import React, { useEffect, useMemo, useState } from "react";

import { isValid } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";

import useGetRaiseCash from "@fartherfinance/frontend/api/Liquidations/hooks/useGetRaiseCash";
import usePutRaiseCash from "@fartherfinance/frontend/api/Liquidations/hooks/usePutRaiseCash";
import useGetTaskActivityLog from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskActivityLog";
import useUpdateTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useUpdateTask.v3";
import type { TaskDetailsV3 } from "@fartherfinance/frontend/api/Tasks/Types";
import {
  AdvisorRequestConfig,
  ClientId,
} from "@fartherfinance/frontend/api/Types";

import { assigneeDefaultOption, taskPriorityOptions } from "../../../common";
import { useServiceTeamsOptions, useTaskStatusOptionsV2 } from "../../../hooks";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import { type TaskDetailsFormDataV3 } from "./TaskDetailsFormProvider.types";
import {
  mapFormDataToPayloadV3,
  mapTaskDetailsToFormDataV3,
} from "./TaskDetailsFormProvider.utils";

interface TaskDetailsFormProviderProps {
  taskDetails: TaskDetailsV3;
}

const combineNameValue = (name: string, value: unknown) =>
  `${name}-${JSON.stringify(value)}`;

const TaskDetailsFormProviderV3: React.FC<
  React.PropsWithChildren<TaskDetailsFormProviderProps>
> = ({ children, taskDetails }) => {
  const [isMutating, setIsMutating] = useState<boolean>(false);

  const auth = useAdvisorRequestAuth();
  const statusNotification = useStatusNotification();
  const callUpdateTaskV3 = useUpdateTaskV3(auth);
  const { refetchTaskActivityLog } = useGetTaskActivityLog(
    taskDetails.id,
    auth as AdvisorRequestConfig
  );

  const { taskStatusFormOptions } = useTaskStatusOptionsV2(
    taskDetails?.taskType
  );
  const { serviceTeamsOptions } = useServiceTeamsOptions();

  const clientId = taskDetails.client
    ? ClientId.parse(taskDetails.client.id)
    : null;

  const putRaiseCash = usePutRaiseCash(auth);
  // get raise cash requests for client - most of the time there won't be any
  const raiseCashRequests = useGetRaiseCash(
    clientId,
    { taskId: taskDetails.id },
    auth
  );
  // get the raise cash request for this task if it exists
  const raiseCashRequest = useMemo(
    () =>
      (raiseCashRequests.data?.data ?? []).find(
        (rc) => rc.taskId === taskDetails.id
      ),
    [raiseCashRequests.data, taskDetails.id]
  );

  const form = useForm<TaskDetailsFormDataV3>({
    mode: "onChange",
    defaultValues: {
      taskStatus: taskStatusFormOptions[0],
      taskPriority: taskPriorityOptions[1],
      assignee: assigneeDefaultOption,
    },
    values:
      taskDetails &&
      mapTaskDetailsToFormDataV3(taskDetails, serviceTeamsOptions),
  });
  const { watch, reset } = form;

  useEffect(() => {
    const { id: taskId, parent: parentTask } = taskDetails ?? {};

    const currentlyUpdatedFields = new Set<string | undefined>();

    const subscription = watch(async (formData, { name }) => {
      const fieldNameAndValue =
        name !== undefined && formData !== undefined
          ? combineNameValue(
              name,
              (formData as TaskDetailsFormDataV3)[
                name as keyof TaskDetailsFormDataV3
              ]
            )
          : "unknown-field";

      if (!taskId) {
        return;
      }

      if (name === "dueDate" && !isValid(formData[name])) {
        return;
      }

      if (currentlyUpdatedFields.has(fieldNameAndValue)) {
        return;
      }

      if (name) {
        try {
          const raiseCashRequestIsInProgress =
            raiseCashRequest?.status === "Initiated" ||
            raiseCashRequest?.status === "PendingLiquidationProposalApproval" ||
            raiseCashRequest?.status === "PendingLiquidationTaskCompletion";

          const statusIsCompletedOrCanceled =
            formData.taskStatus?.value === "COMPLETED" ||
            formData.taskStatus?.value === "CANCELED";

          const settingLiquidationTaskStatusToCompleteOrCanceled =
            name === "taskStatus" &&
            formData.taskType?.value === "LIQUIDATION" &&
            statusIsCompletedOrCanceled;

          if (
            settingLiquidationTaskStatusToCompleteOrCanceled &&
            raiseCashRequest !== undefined &&
            raiseCashRequestIsInProgress
          ) {
            try {
              setIsMutating(true);

              await putRaiseCash({
                planAssignmentId: raiseCashRequest.concreteImplementationPlanId,
                body: {
                  taskId,
                  status:
                    formData.taskStatus?.value === "COMPLETED"
                      ? "Complete"
                      : "Canceled",
                },
              });

              statusNotification("Raise Cash request updated", "Success");
            } catch (raiseCashUpdateError) {
              console.error(raiseCashUpdateError);
              throw new Error("Error updating Raise Cash task");
            } finally {
              setIsMutating(false);
            }
          }

          currentlyUpdatedFields.add(fieldNameAndValue);
          await callUpdateTaskV3({
            taskId,
            request: mapFormDataToPayloadV3(
              formData as TaskDetailsFormDataV3,
              name as keyof TaskDetailsFormDataV3
            ),
            parentTaskId: parentTask?.id,
            queryParams: {
              useDefaultServiceTeam:
                !formData.serviceTeam && !formData.assignee?.value,
            },
          });

          trackEvent({ name: "Advisor Tasks Update" });
        } catch (error) {
          // NOTE: reset the form to the default values in case the update failed
          reset();

          statusNotification(
            extractAxiosErrorMessage(error, "Failed to update task."),
            "Error"
          );
        } finally {
          currentlyUpdatedFields.delete(fieldNameAndValue);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    reset,
    callUpdateTaskV3,
    statusNotification,
    taskDetails,
    refetchTaskActivityLog,
    raiseCashRequest,
    putRaiseCash,
  ]);

  return (
    <>
      {isMutating && <LogoLoadingStill onTop={true} />}

      <FormProvider {...form}>{children}</FormProvider>
    </>
  );
};

export default TaskDetailsFormProviderV3;
